import React, { useState } from 'react';
import { Table, Card, Space, Button, Tag, message, DatePicker, Select, Input } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery, useLazyQuery } from '@apollo/client';
import { GET_ALL_SETTLEMENT_CLIENT_FOR_OPTIONS, type AllSettlementClientResponse } from './graphql/queries/getAllSettlementClient';
import type { ColumnsType } from 'antd/es/table';
import { GET_EXPORT_HISTORY, GET_EXPORT_FILE_DOWNLOAD_URL } from '../../../graphql/queries/settlement/billExportQueries';
import { BillExportHistoryType } from '../../../graphql/types/settlement/BillExportTypes';
import { downloadBillExportFile, formatFileSize, formatExportStatus } from '../../../utils/billExportDownload';
import { formatFileType, getFileTypeColor } from '../../../utils/fileTypeFormatter';
import { settlementClientRoleTypeOptions, SettlementClientRoleType, type SettlementClientRoleTypeValue } from './config/settlementClientRoleType';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';

const { RangePicker } = DatePicker;

/**
 * 账单导出历史页面
 */
const BillExportHistory: React.FC = () => {
  const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]);
  const { t } = useTranslation();
  const [filters, setFilters] = useState<{
    billId?: string;
    clientId?: string;
  }>({});
  const [clientRoleType, setClientRoleType] = useState<SettlementClientRoleTypeValue>(SettlementClientRoleType.CHANNEL_CLIENT);
  
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20
  });

  // 查询导出历史
  const { data, loading, refetch } = useQuery(GET_EXPORT_HISTORY, {
    variables: {
      billId: filters.billId || null,
      clientId: filters.clientId || null,
      pageRequest: pagination
    },
    fetchPolicy: 'network-only'
  });

  const historyResponse = data?.getExportHistory;

  const { data: settlementClientData, loading: settlementClientLoading } = useQuery<AllSettlementClientResponse>(
    GET_ALL_SETTLEMENT_CLIENT_FOR_OPTIONS,
    {
      variables: { roleType: clientRoleType },
      fetchPolicy: 'cache-and-network'
    }
  );

  const settlementClientOptions = settlementClientData?.allOptions || [];
  const clientLabel = clientRoleType === SettlementClientRoleType.DSP_CLIENT ? 'DSP' : '客户(channel)';

  // 下载URL查询
  const [getExportFileDownloadUrl] = useLazyQuery(GET_EXPORT_FILE_DOWNLOAD_URL, { fetchPolicy: 'no-cache' });

  // 下载文件
  const handleDownload = async (record: BillExportHistoryType) => {
    if (record.status !== 'SUCCESS') {
      message.warning('只能下载成功的导出文件');
      return;
    }

    if (!record.fileName) {
      message.error('未找到导出文件信息');
      return;
    }

    try {
      message.loading('正在获取下载链接...', 0);
      const { data } = await getExportFileDownloadUrl({
        variables: { taskId: record.taskId }
      });
      message.destroy();

      const downloadUrl = data?.getExportFileDownloadUrl;
      if (downloadUrl) {
        downloadBillExportFile(downloadUrl, record.fileName);
      } else {
        message.error('获取下载链接失败');
      }
    } catch (error) {
      message.destroy();
      console.error('获取下载链接失败:', error);
      message.error('获取下载链接失败');
    }
  };

  // 表格列定义
  const columns: ColumnsType<BillExportHistoryType> = [
    {
      title: '任务ID',
      dataIndex: 'taskId',
      key: 'taskId',
      width: 100
    },
    {
      title: '账单ID',
      dataIndex: 'billId',
      key: 'billId',
      width: 100
    },
    {
      title: '客户名称',
      dataIndex: 'clientName',
      key: 'clientName',
      width: 150
    },
    {
      title: '模板类型',
      dataIndex: 'templateType',
      key: 'templateType',
      width: 120,
      render: (value: string) => {
        const templateMap: Record<string, string> = {
          'TEMU_TEMPLATE': 'Temu模板',
          'GENERAL_TEMPLATE': '通用模板',
          'CUSTOMER_STANDARD_TEMPLATE': '客户标准模板',
          'SLA_TEMPLATE': 'SLA模板'
        };
        return templateMap[value] || value;
      }
    },
    {
      title: '文件类型',
      dataIndex: 'fileType',
      key: 'fileType',
      width: 100,
      render: (fileType: number) => {
        return <Tag color={getFileTypeColor(fileType as 0 | 1 | 2)}>{formatFileType(fileType as 0 | 1 | 2)}</Tag>;
      }
    },
    {
      title: '导出格式',
      dataIndex: 'exportFormat',
      key: 'exportFormat',
      width: 100
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const { text, color } = formatExportStatus(status);
        return <Tag color={color}>{text}</Tag>;
      }
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      width: 80,
      render: (progress: number) => `${progress}%`
    },
    {
      title: '文件名',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 250,
      ellipsis: true
    },
    {
      title: '文件大小',
      dataIndex: 'fileSize',
      key: 'fileSize',
      width: 100,
      render: (size: number | undefined) => size ? formatFileSize(size) : '-'
    },
    {
      title: '操作人',
      dataIndex: 'operatorName',
      key: 'operatorName',
      width: 100
    },
    {
      title: '创建时间 (本地时区)',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (value: string) => value ? new Date(value).toLocaleString('zh-CN') : '-'
    },
    {
      title: '完成时间 (本地时区)',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 200,
      render: (value: string | undefined) => value ? new Date(value).toLocaleString('zh-CN') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_: any, record: BillExportHistoryType) => canOperate ? (
        <Button
          type="link"
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(record)}
          disabled={record.status !== 'SUCCESS'}
          size="small"
        >
          下载
        </Button>
      ) : null
    }
  ];

  // 表格分页变化
  const handleTableChange = (page: number, pageSize: number) => {
    setPagination({ page, pageSize });
  };

  // 刷新
  const handleRefresh = () => {
    refetch();
  };

  // 查询
  const handleSearch = () => {
    setPagination({ page: 1, pageSize: 20 });
    refetch();
  };

  return (
    <div className="p-6">
      <Card title="账单导出历史" className="shadow-sm">
        {/* 筛选条件 */}
        <div className="mb-4">
          <Space wrap>
            <div className="flex items-center gap-2">
              <span className="whitespace-nowrap">账单ID:</span>
              <Input
                placeholder="请输入账单ID"
                value={filters.billId}
                onChange={(e) => setFilters({ ...filters, billId: e.target.value })}
                style={{ width: 200 }}
                allowClear
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="whitespace-nowrap">客户类型:</span>
              <Select
                value={clientRoleType}
                onChange={(value) => {
                  setClientRoleType(value);
                  setFilters({ ...filters, clientId: undefined });
                }}
                options={settlementClientRoleTypeOptions}
                style={{ width: 160 }}
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="whitespace-nowrap">{clientLabel}:</span>
              <div style={{ width: 250 }}>
                <Select
                  value={filters.clientId}
                  onChange={(value) => setFilters({ ...filters, clientId: value || undefined })}
                  placeholder={`请选择${clientLabel}`}
                  options={settlementClientOptions}
                  showSearch
                  optionFilterProp="label"
                  loading={settlementClientLoading}
                  allowClear
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            {canOperate && (
              <Button type="primary" onClick={handleSearch}>
                查询
              </Button>
            )}
            {canOperate && (
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                刷新
              </Button>
            )}
          </Space>
        </div>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={historyResponse?.content || []}
          rowKey="taskId"
          loading={loading}
          scroll={{ x: 1800 }}
          pagination={false}
         className="mb-4"/>

        {/* 分页 */}
        <TablePaginationBar
          total={historyResponse?.totalElements || 0}
          current={pagination.page}
          pageSize={pagination.pageSize}
          onChange={handleTableChange}
        />
      </Card>
    </div>
  );
};

export default BillExportHistory;

